/**
 * gobdExport — GoBD-/Z3-konformer Daten-Export-Builder (M9c).
 *
 * Reine, deterministische String-/Zahlen-Transformation OHNE Seiteneffekt
 * (KEINE Firebase-/React-Imports) — voll unit-testbar gegen Soll-CSV-Fixtures.
 *
 * Erzeugt pro Entität (invoices, payments, expenses, dunning_events) ein
 * UTF-8-CSV mit klaren Spalten-Headern. Maschinell auswertbar (Z3-tauglich).
 * Periodenfilter (fromISO/toISO inklusive) ist integriert.
 *
 * Audit-Trail-Export (audit_logs) ist CLIENT-SEITIG NICHT MÖGLICH:
 * `audit_logs` hat `read: if false` in den Firestore Security Rules (M0/M9a).
 * Eine spätere Version liefert diesen Teil über einen Backend-Endpoint (PHP-
 * Microservice oder Cloud Function mit Admin-SDK). Dieses Modul enthält einen
 * dafür vorgesehenen Platzhalter und eine Hinweismeldung (auditTrailNote).
 *
 * Integer-Cent-Disziplin durchgängig; Formatierung als "1190,00" am CSV-Rand.
 * buildGobdExport() ist TOTAL (wirft nie) — leere/fehlerhafte Daten → leere
 * Datensatz-Blöcke, KEIN Throw.
 *
 * @module gobdExport
 */

// ─── Periode ─────────────────────────────────────────────────────────────────────

/** ISO-Zeitraum für den Export (inklusive von/bis). */
export interface GobdPeriod {
  /** ISO YYYY-MM-DD, inklusive untere Periodengrenze. */
  fromISO: string;
  /** ISO YYYY-MM-DD, inklusive obere Periodengrenze. */
  toISO: string;
}

// ─── Entkoppelte Eingabe-Typen (von Firestore-Doc-Form entkoppelt) ────────────────

/**
 * Ausgangsrechnung für den GoBD-Export.
 * Entspricht einem gemappten `invoices`-Firestore-Dokument.
 */
export interface GobdInvoiceInput {
  id: string;
  invoiceNumber: string | null;
  dateISO: string;
  buyerName: string;
  /** Gesamtbetrag in Integer-Cents (niemals Float). */
  totalAmountCents: number;
  currency: string;
  isCancellation: boolean;
}

/**
 * Zahlung (inkl. Storno-Zahlungen mit negativem amountCents) für den GoBD-Export.
 * Entspricht einem gemappten `payments`-Firestore-Dokument.
 */
export interface GobdPaymentInput {
  id: string;
  invoiceId: string;
  /** Integer-Cents; negativ bei Storno-Zahlungen (reversalOfPaymentId gesetzt). */
  amountCents: number;
  paidAtISO: string;
  method: string;
  source: string;
  /** Referenz auf die ursprüngliche Zahlung — nur bei Storno-Zahlungen. */
  reversalOfPaymentId?: string | undefined;
}

/**
 * Ausgaben-Buchung (Eingangsbeleg) für den GoBD-Export.
 * Entspricht einem gemappten `expenses`-Firestore-Dokument.
 */
export interface GobdExpenseInput {
  id: string;
  invoiceNumber: string | null;
  dateISO: string;
  supplierName: string;
  /** Bruttobetrag in Integer-Cents. */
  grossCents: number;
  /** Nettobetrag in Integer-Cents. */
  netCents: number;
  /** Umsatzsteuer in Integer-Cents. */
  vatOwedCents: number;
  status: 'draft' | 'booked';
  taxScheme: string;
}

/**
 * Mahnereignis für den GoBD-Export.
 * Entspricht einem gemappten `dunning_events`-Firestore-Dokument.
 */
export interface GobdDunningInput {
  id: string;
  invoiceId: string;
  level: number;
  sentAtISO: string;
  channel: string;
  /** Mahngebühr in Integer-Cents. */
  feeCents: number;
  /** Zinsbetrag in Integer-Cents. */
  interestCents: number;
}

/** Gebündelter Eingabe-Datensatz für buildGobdExport(). */
export interface GobdExportData {
  invoices: GobdInvoiceInput[];
  payments: GobdPaymentInput[];
  expenses: GobdExpenseInput[];
  dunningEvents: GobdDunningInput[];
}

/** Ergebnis eines buildGobdExport()-Aufrufs. */
export interface GobdExportResult {
  /** UTF-8-CSV für Ausgangsrechnungen (Semikolon-getrennt, BOM-fähig). */
  invoicesCsv: string;
  /** UTF-8-CSV für Zahlungen (inkl. Storno-Zahlungen mit negativem Betrag). */
  paymentsCsv: string;
  /** UTF-8-CSV für Ausgaben-Buchungen. */
  expensesCsv: string;
  /** UTF-8-CSV für Mahnereignisse. */
  dunningEventsCsv: string;
  /** Verwendeter Zeitraum (nach Periodenfilter). */
  period: GobdPeriod;
  /** ISO-Zeitstempel der Erzeugung. */
  generatedAtISO: string;
  /** Anzahl der enthaltenen Datensätze je Entität (nach Periodenfilter). */
  counts: {
    invoices: number;
    payments: number;
    expenses: number;
    dunningEvents: number;
  };
  /**
   * Hinweis für den Nutzer/Prüfer: Audit-Trail-Export (audit_logs) ist
   * client-seitig nicht verfügbar (Firestore-Rule: read=if-false). Wird
   * in einer späteren Version über einen Backend-Endpoint bereitgestellt.
   */
  auditTrailNote: string;
}

// ─── Reine Helfer ─────────────────────────────────────────────────────────────────

/**
 * Periodenfilter: liegt dateISO (YYYY-MM-DD) innerhalb [fromISO, toISO] (inklusive)?
 * Lexikografischer Vergleich auf den ersten 10 ISO-Zeichen — keine Zeitzonen-Falle.
 */
function inPeriod(dateISO: string, period: GobdPeriod): boolean {
  if (!dateISO) return false;
  const d = dateISO.slice(0, 10);
  return d >= period.fromISO.slice(0, 10) && d <= period.toISO.slice(0, 10);
}

/**
 * Formatiert Integer-Cents als Betragswert mit Komma-Dezimal (kein Tausender-Trenner).
 * Negatives Vorzeichen bleibt erhalten (Storno-Zahlungen mit negativem amountCents).
 * Reine Integer-Arithmetik (keine Float-Formatierung).
 *
 * @example formatAmount(119000)  // '1190,00'
 * @example formatAmount(-59500)  // '-595,00'
 * @example formatAmount(0)       // '0,00'
 */
export function formatAmount(cents: number): string {
  const sign = cents < 0 ? '-' : '';
  const a = Math.abs(Math.trunc(cents));
  const euros = Math.floor(a / 100);
  const rem = a % 100;
  return `${sign}${euros},${String(rem).padStart(2, '0')}`;
}

/**
 * Maskiert ein CSV-Textfeld: CR/LF → Leerzeichen, `"` → `""`, gesamtes Feld in Quotes.
 * Leere/null/undefined-Werte → leeres Paar-Quotes `""`.
 */
function quoteField(value: string | null | undefined): string {
  if (value === null || value === undefined || String(value).length === 0) return '""';
  const cleaned = String(value).replace(/[\r\n]+/g, ' ').replace(/"/g, '""');
  return `"${cleaned}"`;
}

/**
 * Serialisiert eine Datenzeile (Array gemischter Werte) als Semikolon-getrennte CSV-Zeile.
 * Strings/null → quoteField(), Numbers → direkte Stringifizierung (ohne Quotes).
 */
function toRow(fields: (string | number | null | undefined)[]): string {
  return fields
    .map((f) => (typeof f === 'number' ? String(f) : quoteField(f)))
    .join(';');
}

/**
 * Baut einen vollständigen UTF-8-CSV-String (CRLF-Zeilenenden, Header + Datenzeilen).
 * Keine Seiteneffekte — reine String-Transformation.
 */
function buildCsv(
  header: string[],
  rows: (string | number | null | undefined)[][],
): string {
  const lines: string[] = [toRow(header)];
  for (const row of rows) {
    lines.push(toRow(row));
  }
  return lines.join('\r\n') + '\r\n';
}

// ─── Entitäts-CSV-Builder (privat) ───────────────────────────────────────────────

function buildInvoicesCsv(
  invoices: GobdInvoiceInput[],
  period: GobdPeriod,
): { csv: string; count: number } {
  const header = [
    'Dokument-ID',
    'Rechnungsnummer',
    'Datum',
    'Kaeufer',
    'Gesamtbetrag_EUR',
    'Waehrung',
    'Storno_J_N',
  ];

  const rows: (string | number | null)[][] = [];
  for (const inv of invoices ?? []) {
    if (!inPeriod(inv.dateISO, period)) continue;
    rows.push([
      inv.id,
      inv.invoiceNumber ?? '',
      inv.dateISO.slice(0, 10),
      inv.buyerName,
      formatAmount(inv.totalAmountCents),
      (inv.currency ?? 'EUR').toUpperCase(),
      inv.isCancellation ? 'J' : 'N',
    ]);
  }

  return { csv: buildCsv(header, rows), count: rows.length };
}

function buildPaymentsCsv(
  payments: GobdPaymentInput[],
  period: GobdPeriod,
): { csv: string; count: number } {
  const header = [
    'Dokument-ID',
    'Rechnungs-ID',
    'Betrag_EUR',
    'Zahlungsdatum',
    'Methode',
    'Quelle',
    'Stornoref-ID',
  ];

  const rows: (string | number | null)[][] = [];
  for (const p of payments ?? []) {
    if (!inPeriod(p.paidAtISO, period)) continue;
    rows.push([
      p.id,
      p.invoiceId,
      formatAmount(p.amountCents),
      p.paidAtISO.slice(0, 10),
      p.method,
      p.source,
      p.reversalOfPaymentId ?? '',
    ]);
  }

  return { csv: buildCsv(header, rows), count: rows.length };
}

function buildExpensesCsv(
  expenses: GobdExpenseInput[],
  period: GobdPeriod,
): { csv: string; count: number } {
  const header = [
    'Dokument-ID',
    'Belegnummer',
    'Datum',
    'Lieferant',
    'Brutto_EUR',
    'Netto_EUR',
    'USt_EUR',
    'Status',
    'Steuerart',
  ];

  const rows: (string | number | null)[][] = [];
  for (const e of expenses ?? []) {
    if (!inPeriod(e.dateISO, period)) continue;
    rows.push([
      e.id,
      e.invoiceNumber ?? '',
      e.dateISO.slice(0, 10),
      e.supplierName,
      formatAmount(e.grossCents),
      formatAmount(e.netCents),
      formatAmount(e.vatOwedCents),
      e.status,
      e.taxScheme,
    ]);
  }

  return { csv: buildCsv(header, rows), count: rows.length };
}

function buildDunningEventsCsv(
  events: GobdDunningInput[],
  period: GobdPeriod,
): { csv: string; count: number } {
  const header = [
    'Dokument-ID',
    'Rechnungs-ID',
    'Mahnstufe',
    'Versanddatum',
    'Kanal',
    'Mahngebuehr_EUR',
    'Zinsen_EUR',
  ];

  const rows: (string | number | null)[][] = [];
  for (const ev of events ?? []) {
    if (!inPeriod(ev.sentAtISO, period)) continue;
    rows.push([
      ev.id,
      ev.invoiceId,
      ev.level,
      ev.sentAtISO.slice(0, 10),
      ev.channel,
      formatAmount(ev.feeCents),
      formatAmount(ev.interestCents),
    ]);
  }

  return { csv: buildCsv(header, rows), count: rows.length };
}

// ─── Öffentliche API ─────────────────────────────────────────────────────────────

/**
 * Erzeugt deterministisch den vollständigen GoBD-/Z3-Daten-Export für einen Zeitraum.
 *
 * TOTAL: wirft nie. Leere/fehlerhafte Eingaben → leere CSV-Blöcke mit Header.
 * Periodenfilter ist integriert (nur Datensätze mit Datum in [fromISO, toISO]).
 *
 * HINWEIS Audit-Trail: `audit_logs` ist client-seitig NICHT lesbar (Firestore Rules
 * `read: if false`). Das Feld `auditTrailNote` im Ergebnis enthält einen Hinweis.
 * Der Audit-Trail-Export erfordert einen Backend-Endpoint und ist als Folgearbeit
 * vorgemerkt (architecture-m9.md §6.1).
 *
 * @param data    - Belegdaten je Entität (owner-scoped, bereits aus Firestore geladen).
 * @param period  - Zeitraum (fromISO/toISO inklusive, ISO YYYY-MM-DD).
 * @returns GobdExportResult mit je einem CSV-String pro Entität + Zähler.
 */
export function buildGobdExport(
  data: GobdExportData,
  period: GobdPeriod,
): GobdExportResult {
  const generatedAtISO = new Date().toISOString();

  const { csv: invoicesCsv, count: invCount } = buildInvoicesCsv(
    data.invoices ?? [],
    period,
  );
  const { csv: paymentsCsv, count: payCount } = buildPaymentsCsv(
    data.payments ?? [],
    period,
  );
  const { csv: expensesCsv, count: expCount } = buildExpensesCsv(
    data.expenses ?? [],
    period,
  );
  const { csv: dunningEventsCsv, count: dunCount } = buildDunningEventsCsv(
    data.dunningEvents ?? [],
    period,
  );

  return {
    invoicesCsv,
    paymentsCsv,
    expensesCsv,
    dunningEventsCsv,
    period,
    generatedAtISO,
    counts: {
      invoices: invCount,
      payments: payCount,
      expenses: expCount,
      dunningEvents: dunCount,
    },
    auditTrailNote:
      'Audit-Trail-Export (audit_logs) nicht client-seitig verfügbar ' +
      '(Firestore-Rule: read=if-false, M9a). Folgearbeit: Backend-Endpoint ' +
      '(PHP-Microservice oder Cloud Function mit Admin-SDK).',
  };
}

/**
 * Baut den Dateinamen für eine GoBD-Export-CSV.
 * Format: `GoBD_<entity>_<YYYYMMDD>_<YYYYMMDD>.csv`
 *
 * @param entity  - Entitäts-Bezeichner, z.B. 'Rechnungen', 'Zahlungen'.
 * @param period  - Zeitraum (fromISO/toISO).
 * @returns Dateiname als String.
 */
export function buildGobdFilename(entity: string, period: GobdPeriod): string {
  const from = period.fromISO.slice(0, 10).replace(/-/g, '');
  const to = period.toISO.slice(0, 10).replace(/-/g, '');
  return `GoBD_${entity}_${from}_${to}.csv`;
}

/**
 * Baut den finalen Download-Blob aus einem UTF-8-CSV-String.
 *
 * Fügt ein UTF-8-BOM (0xEF 0xBB 0xBF) vor den Inhalt — notwendig damit
 * Microsoft Excel die Datei beim Öffnen korrekt als UTF-8 erkennt und
 * Umlaute nicht zerschießt.
 *
 * @param csv - CSV-String (CRLF-Zeilenenden, Header + Datenzeilen).
 * @returns Blob mit `text/csv;charset=utf-8`.
 */
export function buildGobdBlob(csv: string): Blob {
  // UTF-8 BOM: U+FEFF (escape only — no literal exotic char in source per golden rule).
  // eslint-disable-next-line no-control-regex
  const bom = String.fromCharCode(0xfeff);
  return new Blob([bom + csv], { type: 'text/csv;charset=utf-8' });
}
